'use client';

import API from '@/API';
import logo from '@/public/logo.png';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Icons } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { generateExpiryToken } from '@/lib/utils';
import { login } from '@/redux/action';
import { IUserSession } from '@/redux/reducer/session';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const creadentailFormSchema = z.object({
  email: z.string().min(1, 'Email is required'),
  password: z.string().min(1, 'Password is required'),
});

const otpFormSchema = z.object({
  otp: z.string().min(6, {
    message: 'Your one-time password must be 6 characters.',
  }),
  email: z.string().min(1, 'Email is required'),
});

const LoginPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [credentailLoading, setCredentailLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // check if user is already logged in
    const userData: IUserSession = localStorage.getItem('userSession')
      ? JSON.parse(localStorage.getItem('userSession') as any)
      : null;
    if (userData && userData?.user && userData?.company) {
      router.push('/dashboard');
    }
  }, []);

  const creadentialForm = useForm({
    resolver: zodResolver(creadentailFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const otpForm = useForm({
    resolver: zodResolver(otpFormSchema),
    defaultValues: {
      otp: '',
      email: '',
    },
  });

  const onCredentailSubmit = (data: any) => {
    setCredentailLoading(true);

    API.auth
      .sendOtp(data)
      .then((response) => {
        if (response) {
          if (response?.to_change_password) {
            return router.push('/reset-password');
          }
          otpForm.setValue('email', data.email);
          setStep(1);
        }
      })
      .finally(() => {
        setCredentailLoading(false);
      });
  };

  const onSubmit = (data: any) => {
    setOtpLoading(true);
    API.auth
      .verifyOtp(data)
      .then((response) => {
        if (response) {
          response.token_expiry = generateExpiryToken();
          dispatch(login(response));
          if (response?.to_change_password == true) {
            router.push('/reset-password');
          } else router.push('/dashboard');
        }
      })
      .finally(() => {
        setOtpLoading(false);
      });
  };

  return (
    <div className='flex h-screen flex-col md:flex-row'>
      <div className='flex h-48 w-full items-center justify-center bg-muted text-5xl font-semibold md:h-full md:w-2/3'>
        <Image
          src={logo}
          alt='logo'
          width={400}
          height={200}
          className='w-96 object-contain'
        />
      </div>
      <div className='h-full w-full items-center justify-center p-4'>
        <div className='flex h-full flex-col items-center justify-center'>
          <h2 className='mb-4 text-3xl font-semibold md:text-4xl'>
            Welcome back!
          </h2>
          {step === 0 ? (
            <CredentialForm
              form={creadentialForm}
              onSubmit={onCredentailSubmit}
              loading={credentailLoading}
            />
          ) : (
            <OtpForm form={otpForm} onSubmit={onSubmit} loading={otpLoading} />
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

const CredentialForm = ({
  form,
  onSubmit,
  loading,
}: {
  form: UseFormReturn<
    {
      email: string;
      password: string;
    },
    any,
    undefined
  >;
  onSubmit: (data: any) => void;
  loading: boolean;
}) => {
  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='w-full max-w-xl space-y-4'
        >
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder='Email' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input placeholder='Password' {...field} type='password' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <p className='text-sm text-muted-foreground'>
            By continuing, you agree to our{' '}
            <Link href='term' className='text-blue-500'>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href='privacy' className='text-blue-500'>
              Privacy Policy
            </Link>
            .
          </p>
          <div className='flex items-center justify-center gap-10'>
            <Button type='submit'>
              {loading && (
                <Icons.spinner className='mr-2 h-4 w-4 animate-spin' />
              )}
              Login
              <ArrowRight className='ml-2 h-4 w-4' />
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};

const OtpForm = ({
  form,
  onSubmit,
  loading,
}: {
  form: UseFormReturn<
    {
      otp: string;
      email: string;
    },
    any,
    undefined
  >;
  onSubmit: (data: any) => void;
  loading: boolean;
}) => {
  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='w-full max-w-xl space-y-4'
        >
          <FormField
            control={form.control}
            name='otp'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel>One-Time Password</FormLabel>
                <FormControl className='flex w-full flex-col items-center'>
                  <InputOTP maxLength={6} {...field} className='h-24'>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormDescription>
                  Please enter the one-time password sent to your email.
                </FormDescription>
                <FormDescription>
                  It may take a few minutes for the email to arrive.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className='flex items-center justify-start gap-10'>
            <Button type='submit'>
              {loading && (
                <Icons.spinner className='mr-2 h-4 w-4 animate-spin' />
              )}
              Submit
              <ArrowRight className='ml-2 h-4 w-4' />
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};
